import { t } from "i18next";
import { Eye } from "lucide-react";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { KycCaseDto } from "@/features/kyc/api/dto";
import {
  KYC_CASE_STATUSES,
  KYC_CHANNELS,
} from "@/features/shared/api/enums";

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

/**
 * Colonnes de la liste des dossiers KYC (`POST /KycCases/all`) — file de
 * travail du back-office. Filtres select sur les énumérations de l'annexe A ;
 * identifiants et données métier en `NoTranslate` (règle notranslate).
 */
export const kycCasesColumns: ColumnsClarFactory<KycCaseDto> = (navigate) => [
  {
    id: "id",
    accessorKey: "id",
    header: t("kyc.cases.columns.id"),
    cell: ({ getValue }) => {
      const id = getValue() as string;
      return (
        <NoTranslate as="span" className="font-mono text-xs">
          {id ? `${id.slice(0, 8)}…` : "—"}
        </NoTranslate>
      );
    },
    // Pas dans KycCaseFilter.
    meta: { type: "denied" },
  },
  {
    id: "partyId",
    accessorKey: "partyId",
    header: t("kyc.cases.columns.partyId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "levelRequested",
    accessorKey: "levelRequested",
    header: t("kyc.cases.columns.levelRequested"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    // Pas dans KycCaseFilter (guide §6.2).
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("kyc.cases.columns.status"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(KYC_CASE_STATUSES) },
  },
  {
    id: "channel",
    accessorKey: "channel",
    header: t("kyc.cases.columns.channel"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(KYC_CHANNELS) },
  },
  {
    id: "submittedAt",
    accessorKey: "submittedAt",
    header: t("kyc.cases.columns.submittedAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <RowActionsMenu
          actions={[
            {
              id: "view",
              label: t("kyc.cases.actions.view"),
              icon: Eye,
              onClick: () =>
                navigate(`/app/kyc/cases/${row.original.id}`),
            },
          ]}
        />
      ) : null,
    meta: { type: "denied" },
  },
];
